import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import { useUserContext } from 'App/contexts/UserContext';
import { saveReferralEhrNote } from 'App/services/AMSService';
import { branchSettingsPropType, patientPropType } from 'Shared/types';
import FormButtonGroup from '../styles/FormButtonGroup';
import EhrEncounterVisitContent from './EhrEncounterVisitContent';
import LaddaButton from '../LaddaButton';

const ehrModalAction = Object.freeze({
    review: 'review',
    associateVisit: 'associateVisit',
});

const EhrEncounterNoteModal = ({ activity, patient, branchSettings, onComplete, activityStatus, children }) => {
    const { values, setFieldTouched, validateForm, isValid } = useFormikContext();
    const user = useUserContext();
    const ehrMessage = useRef();
    const [modalState, setModalState] = useState(ehrModalAction.review);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const modalStateToComponent = {
        [ehrModalAction.review]: children,
        [ehrModalAction.associateVisit]: <EhrEncounterVisitContent patient={patient} />,
    };

    const modalStateToTitle = {
        [ehrModalAction.review]: activity.name,
        [ehrModalAction.associateVisit]: 'Associate Visit',
    };

    const EHRNoteRequiresVisit = branchSettings.ehrNoteRequiresVisit;

    async function handleSubmitButton(sendToEhr) {
        setIsSubmitting(true);
        if (!isValid) {
            setIsSubmitting(false);
            return;
        }
        if (modalState === ehrModalAction.review) {
            ehrMessage.current = `<div>${document.getElementsByClassName('modal-header')[0].outerHTML}${
                document.getElementsByClassName('modal-body')[0].outerHTML
            }</div>`;
        }
        if (EHRNoteRequiresVisit && modalState === ehrModalAction.review && sendToEhr) {
            setModalState(ehrModalAction.associateVisit);
            await setFieldTouched('associatedVisit');
            setImmediate(() => validateForm());
            setIsSubmitting(false);
        } else {
            const payload = {
                message: ehrMessage.current,
                ehrProviderId: user.user_attributes.ehrProviderId,
                visitId: values.associatedVisit?.visitId,
                facilityId: values.associatedVisit?.facilityId,
                prescriberId: values.associatedPrescriber?.id,
                sendToEhr,
                activityStatus,
            };
            try {
                await saveReferralEhrNote(activity.id, payload);
                await onComplete();
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    return (
        <Modal show>
            <Modal.Header>
                <Modal.Title role="heading">{modalStateToTitle[modalState]}</Modal.Title>
            </Modal.Header>

            <Modal.Body id="ehr-note-body">{modalStateToComponent[modalState]}</Modal.Body>

            <Modal.Footer>
                <FormButtonGroup>
                    <Button variant="outline-danger" disabled={isSubmitting} onClick={() => handleSubmitButton(false)}>
                        Don&apos;t Send to EHR
                    </Button>
                    <div style={{ flexGrow: 1 }} />
                    <Button variant="outline-secondary" disabled={isSubmitting} onClick={() => onComplete()}>
                        Cancel
                    </Button>
                    <LaddaButton
                        isLoading={isSubmitting}
                        id="ehr-note-send-btn"
                        variant="primary"
                        onClick={() => handleSubmitButton(true)}
                    >
                        {EHRNoteRequiresVisit && modalState === ehrModalAction.review ? 'Next' : 'Send to EHR'}
                    </LaddaButton>
                </FormButtonGroup>
            </Modal.Footer>
        </Modal>
    );
};

EhrEncounterNoteModal.propTypes = {
    activity: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    patient: patientPropType.isRequired,
    branchSettings: branchSettingsPropType.isRequired,
    onComplete: PropTypes.func.isRequired,
    activityStatus: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

export default EhrEncounterNoteModal;
